/**
 * MBTI derivatives — the single place that knows which frameworks are a pure
 * function of the MBTI type.
 *
 * Socionics, Keirsey and the cognitive function stack are deterministic maps of
 * the four MBTI letters, so whenever the effective MBTI changes they MUST be
 * re-derived. Frameworks scored from the TPS trait vector (Holland, DiSC,
 * Attachment, Big Five, Hogwarts, Love Languages, …) are intentionally NOT
 * derived here — an MBTI correction must not silently rewrite them.
 */

export const MBTI_TO_SOCIONICS_MAPPING: Record<string, string> = {
  INTJ: 'INTp (ILI)',
  INTP: 'INTj (LII)',
  ENTJ: 'ENTj (LIE)',
  ENTP: 'ENTp (ILE)',
  INFJ: 'INFp (IEI)',
  INFP: 'INFj (EII)',
  ENFJ: 'ENFj (EIE)',
  ENFP: 'ENFp (IEE)',
  ISTJ: 'ISTp (SLI)',
  ISFJ: 'ISFp (SEI)',
  ESTJ: 'ESTj (LSE)',
  ESFJ: 'ESFj (ESE)',
  ISTP: 'ISTj (LSI)',
  ISFP: 'ISFj (ESI)',
  ESTP: 'ESTp (SLE)',
  ESFP: 'ESFp (SEE)',
};

export const TYPE_COGNITIVE_STACKS: Record<string, [string, string, string, string]> = {
  INTJ: ['Ni', 'Te', 'Fi', 'Se'],
  INTP: ['Ti', 'Ne', 'Si', 'Fe'],
  ENTJ: ['Te', 'Ni', 'Se', 'Fi'],
  ENTP: ['Ne', 'Ti', 'Fe', 'Si'],
  INFJ: ['Ni', 'Fe', 'Ti', 'Se'],
  INFP: ['Fi', 'Ne', 'Si', 'Te'],
  ENFJ: ['Fe', 'Ni', 'Se', 'Ti'],
  ENFP: ['Ne', 'Fi', 'Te', 'Si'],
  ISTJ: ['Si', 'Te', 'Fi', 'Ne'],
  ISFJ: ['Si', 'Fe', 'Ti', 'Ne'],
  ESTJ: ['Te', 'Si', 'Ne', 'Fi'],
  ESFJ: ['Fe', 'Si', 'Ne', 'Ti'],
  ISTP: ['Ti', 'Se', 'Ni', 'Fe'],
  ISFP: ['Fi', 'Se', 'Ni', 'Te'],
  ESTP: ['Se', 'Ti', 'Fe', 'Ni'],
  ESFP: ['Se', 'Fi', 'Te', 'Ni'],
};

export const MBTI_TO_KEIRSEY: Record<string, { temperament: string; role: string }> = {
  ESTJ: { temperament: 'Guardian', role: 'Supervisor' },
  ISTJ: { temperament: 'Guardian', role: 'Inspector' },
  ESFJ: { temperament: 'Guardian', role: 'Provider' },
  ISFJ: { temperament: 'Guardian', role: 'Protector' },
  ESTP: { temperament: 'Artisan', role: 'Promoter' },
  ISTP: { temperament: 'Artisan', role: 'Crafter' },
  ESFP: { temperament: 'Artisan', role: 'Performer' },
  ISFP: { temperament: 'Artisan', role: 'Composer' },
  ENFJ: { temperament: 'Idealist', role: 'Teacher' },
  INFJ: { temperament: 'Idealist', role: 'Counselor' },
  ENFP: { temperament: 'Idealist', role: 'Champion' },
  INFP: { temperament: 'Idealist', role: 'Healer' },
  ENTJ: { temperament: 'Rational', role: 'Fieldmarshal' },
  INTJ: { temperament: 'Rational', role: 'Mastermind' },
  ENTP: { temperament: 'Rational', role: 'Inventor' },
  INTP: { temperament: 'Rational', role: 'Architect' },
};

/** Normalize to a canonical 4-letter uppercase MBTI code, or null. */
export function normalizeMbtiCode(value: unknown): string | null {
  const raw =
    typeof value === 'string'
      ? value
      : value && typeof value === 'object'
        ? ((value as { type?: string }).type ?? '')
        : '';
  const code = raw.trim().toUpperCase().slice(0, 4);
  if (!/^[EI][SN][TF][JP]$/.test(code)) return null;
  return code;
}

/** Deterministic derivatives of an MBTI type. */
export function deriveMbtiDerivatives(mbti: unknown): {
  mbti: string;
  socionics: string;
  keirsey: string;
  keirseyLabel: string;
  cognitiveStack: [string, string, string, string];
} | null {
  const code = normalizeMbtiCode(mbti);
  if (!code) return null;
  const keirsey = MBTI_TO_KEIRSEY[code];
  return {
    mbti: code,
    socionics: MBTI_TO_SOCIONICS_MAPPING[code],
    keirsey: keirsey.temperament,
    keirseyLabel: `${keirsey.temperament} (${keirsey.role})`,
    cognitiveStack: TYPE_COGNITIVE_STACKS[code],
  };
}

/** True when the stored Socionics string does not match the MBTI-derived one. */
export function isSocionicsStale(mbti: unknown, socionics: unknown): boolean {
  const derived = deriveMbtiDerivatives(mbti);
  if (!derived) return false;
  if (typeof socionics !== 'string' || !socionics.trim()) return true;
  return socionics.trim().toLowerCase() !== derived.socionics.toLowerCase();
}

const DEFAULT_STACK_STRENGTHS = [7, 5.5, 4, 3];

/**
 * Rebuild the full MBTI detail block so it can never contradict the effective
 * type.
 *
 * Relabelling only the four stack slots (what we used to do) left the previous
 * run's numbers, preference axes and mismatch warnings in place, so an ENFJ
 * report could show "Fe 9.0" while the eight-function list said Fe 6.8, and an
 * amber banner still argued for the old letters. Here we:
 *  - read each stack slot's strength from the measured per-function scores,
 *  - regenerate the four preference axes from the effective type's letters,
 *    keeping measured confidence only where the measured letter agrees,
 *  - drop every field that describes a different type.
 */
export function rebuildMbtiDetail(
  mbti: unknown,
  existing: any,
  source: 'pinned' | 'canonical' | 'derived' = 'derived',
): any | null {
  const code = normalizeMbtiCode(mbti);
  if (!code) return null;
  const stack = TYPE_COGNITIVE_STACKS[code];
  const prev = existing ?? {};
  const strengths: Record<string, number> | undefined =
    prev.functionStrengths && typeof prev.functionStrengths === 'object'
      ? (prev.functionStrengths as Record<string, number>)
      : undefined;

  const slot = (fn: string, i: number) => {
    const measured = strengths ? Number(strengths[fn]) : NaN;
    return {
      function: fn,
      strength: Number.isFinite(measured) ? measured : DEFAULT_STACK_STRENGTHS[i],
      measured: Number.isFinite(measured),
    };
  };

  // Preference axes aligned to the effective letters.
  const AXES: Array<[string, number]> = [['EI', 0], ['SN', 1], ['TF', 2], ['JP', 3]];
  const prevPrefs = prev.preferences && typeof prev.preferences === 'object' ? prev.preferences : {};
  const preferences: Record<string, { letter: string; confidence: number | null; source?: string }> = {};
  for (const [axis, idx] of AXES) {
    const letter = code[idx];
    const before: any = (prevPrefs as any)[axis];
    const agrees = before?.letter === letter;
    preferences[axis] = agrees
      ? { letter, confidence: before?.confidence ?? null }
      : { letter, confidence: null, source };
  }

  const sameType = (v: unknown) =>
    typeof v === 'string' && normalizeMbtiCode(v) === code;

  const rebuilt: any = {
    ...prev,
    type: code,
    typeSource: source,
    preferences,
    cognitiveFunctions: {
      dominant: slot(stack[0], 0),
      auxiliary: slot(stack[1], 1),
      tertiary: slot(stack[2], 2),
      inferior: slot(stack[3], 3),
    },
  };

  // Anything describing a different type is stale — remove it rather than
  // print a warning about letters the user no longer has.
  if (!(sameType(prev.dichotomyType) && sameType(prev.functionType))) {
    delete rebuilt.hasMismatch;
    delete rebuilt.dichotomyType;
    delete rebuilt.functionType;
  }
  if (prev.tiedWith && !sameType(prev.tiedWith?.type)) {
    delete rebuilt.tiedWith;
    delete rebuilt.contested;
  }
  if (!sameType(prev.type)) {
    delete rebuilt.stackFit;
    delete rebuilt.alternateTypes;
    delete rebuilt.unresolvedAxes;
  }

  return rebuilt;
}